import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth-context';
import { useDataService } from '@/lib/service-context';
import { Button } from '@erp/ui';
import { Input } from '@erp/ui';
import { Label } from '@erp/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@erp/ui';
import {
  fetchPlatformAdminAccess,
  PLATFORM_ADMIN_ACCESS_KEY,
} from '@/hooks/use-platform-admin';
import {
  hasActivePlatformAdminTenantViewAccess,
  hasPlatformAdminTenantHandoffIntent,
} from '@/lib/admin-tenant-view';
import { isPlatformAdminHost, isPlatformAdminMode } from '@/lib/platform-admin';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const ds = useDataService();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const platformAdminMode = isPlatformAdminMode();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const hadAdminTenantContext =
      hasActivePlatformAdminTenantViewAccess() || hasPlatformAdminTenantHandoffIntent();

    try {
      await login(email.trim().toLowerCase(), password);

      if (platformAdminMode) {
        const adminTarget = isPlatformAdminHost() ? '/' : '/platform';
        if (hadAdminTenantContext) {
          window.location.assign(adminTarget);
          return;
        }
        void qc.cancelQueries({ queryKey: PLATFORM_ADMIN_ACCESS_KEY });
        qc.removeQueries({ queryKey: PLATFORM_ADMIN_ACCESS_KEY });
        const hasPlatformAdminAccess = await qc.fetchQuery({
          queryKey: PLATFORM_ADMIN_ACCESS_KEY,
          queryFn: () => fetchPlatformAdminAccess(ds),
          staleTime: 5 * 60 * 1000,
        });
        if (!hasPlatformAdminAccess) {
          throw new Error('platform admin access denied');
        }
        navigate(adminTarget, { replace: true });
      } else {
        // Rebuild after dashboard login so auth, tenant, branding, and data
        // clients all read the fresh session instead of stale boot state.
        window.location.assign('/');
        return;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(humanizeAuthError(msg, t));
      console.warn('[auth] login failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  function humanizeAuthError(message: string, t: (k: string) => string): string {
    const lower = message.toLowerCase();
    if (lower.includes('invalid login credentials') || lower.includes('invalid grant')) {
      return t('auth.loginError');
    }
    if (lower.includes('email not confirmed')) {
      return 'Email pas encore confirmé — vérifiez votre boîte de réception.';
    }
    if (lower.includes('rate limit') || lower.includes('too many')) {
      return 'Trop de tentatives — patientez quelques minutes.';
    }
    if (lower.includes('platform admin access denied')) {
      return 'Ce compte n a pas acces a la console admin.';
    }
    if (lower.includes('platform admin access') && lower.includes('timeout')) {
      return 'La verification admin a pris trop de temps. Reessayez dans quelques secondes.';
    }
    if (lower.includes('fetch') || lower.includes('network')) {
      return 'Pas de réseau. Vérifiez votre connexion.';
    }
    return `${t('auth.loginError')} (${message})`;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {platformAdminMode ? 'Gesty Admin' : t('auth.login')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('auth.loginButton')}
            </Button>
          </form>
          <div className="mt-3 text-right">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:underline">
              {t('auth.forgotLink', 'Mot de passe oublié ?')}
            </Link>
          </div>
          {!platformAdminMode ? (
            <div className="mt-4 border-t pt-4 text-center text-sm text-muted-foreground">
              Pas encore de compte ?{' '}
              <Link to="/signup" className="text-primary hover:underline">
                Créer ma boutique
              </Link>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
